import React, { Suspense, useEffect, useState } from "react";
import { connect } from "react-redux";
import SideNav from "components/layout-components/SideNav";
import TopNav from "components/layout-components/TopNav";
import Loading from "components/shared-components/Loading";
import MobileNav from "components/layout-components/MobileNav";
import PageHeader from "components/layout-components/PageHeader";
import AppViews from "views/app-views";
import { Layout, Grid, Spin } from "antd";
import { RightOutlined, LeftOutlined } from "@ant-design/icons";

import navigationConfig from "configs/NavigationConfig";
import {
  SIDE_NAV_WIDTH,
  SIDE_NAV_COLLAPSED_WIDTH,
  NAV_TYPE_SIDE,
  NAV_TYPE_TOP,
} from "constants/ThemeConstant";
import utils from "utils";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { useLocation } from "react-router-dom";
import { FORMS, LANDING_PAGE_BUILDER } from "constants/PageSlugConstants";
import CallNotification from "components/layout-components/CallNotification";
import { MOMBILE_BREAKPOINT } from "constants/ConversationConstants";
import useWindowSize from "hooks/useIsMobile";
import useIsFeatureClosed from "hooks/useIsFeatureClosed";
import { useGetSquareConnectStatus } from "queries/payments";
import { toggleCollapsedNav, onMobileNavToggle } from "redux/actions/Theme";
import {
  CHARTING_NAVIGATION_KEY_APP_VIEW,
  FEATURE_LIST,
  MEMBERSHIP_FEATURE_KEY,
  MEMBERSHIP_NAVIGATION_IND,
  MY_PRACTICE_NAVIGATION_KEY_APP_VIEW,
  QUESTIONNAIRES_NAVIGATION_KEY_APP_VIEW,
  SERVICE_REPORTING_FEATURE_KEY,
  SERVICE_REPORTING_IND,
  TRANSACTION_REPORTING_FEATURE_KEY,
  LOYALTY_NAVIGATION_KEY_APP_VIEW,
  MARKETING_NAVIGATION_KEY_APP_VIEW,
  SPECIALS_NAVIGATION_KEY_APP_VIEW,
  GIFT_CARDS_NAVIGATION_KEY_APP_VIEW,
} from "constants/FeatureControlContstants";

const { Content } = Layout;
const { useBreakpoint } = Grid;

export const AppLayout = ({
  navCollapsed,
  navType,
  location,
  mobileNav,
  toggleCollapsedNav,
  onMobileNavToggle,
}) => {
  const isMembershipFeatureClosed = useIsFeatureClosed(
    FEATURE_LIST.MEMBERSHIPS
  );
  const isReportingStatsClosed = useIsFeatureClosed(
    FEATURE_LIST.SERVICE_REPORTING
  );
  const isChartingFeatureClosed = useIsFeatureClosed(FEATURE_LIST.CHARTING);
  const isQuestionnairesFeatureClosed = useIsFeatureClosed(
    FEATURE_LIST.QUESTIONNAIRES
  );
  const isLoyaltyFeatureClosed = useIsFeatureClosed(FEATURE_LIST.LOYALTY);
  const isSpecialsFeatureClosed = useIsFeatureClosed(FEATURE_LIST.SPECIALS);
  const isGiftCardsFeatureClosed = useIsFeatureClosed(FEATURE_LIST.GIFT_CARDS);
  const { connected: isSquareConnected } = useGetSquareConnectStatus();

  if (isMembershipFeatureClosed) {
    navigationConfig[MEMBERSHIP_NAVIGATION_IND].submenu = navigationConfig[
      MEMBERSHIP_NAVIGATION_IND
    ].submenu?.filter((item) => item.key !== MEMBERSHIP_FEATURE_KEY);
  }

  if (isReportingStatsClosed) {
    navigationConfig[SERVICE_REPORTING_IND].submenu = navigationConfig[
      SERVICE_REPORTING_IND
    ].submenu?.filter((item) => item.key !== SERVICE_REPORTING_FEATURE_KEY);
  }

  if (isSquareConnected) {
    navigationConfig[SERVICE_REPORTING_IND].submenu = navigationConfig[
      SERVICE_REPORTING_IND
    ].submenu?.filter((item) => item.key !== TRANSACTION_REPORTING_FEATURE_KEY);
  }

  if (isChartingFeatureClosed) {
    const myPracticeMenuIndex = navigationConfig.findIndex(
      (item) => item.key === MY_PRACTICE_NAVIGATION_KEY_APP_VIEW
    );
    navigationConfig[myPracticeMenuIndex].submenu = navigationConfig[
      myPracticeMenuIndex
    ].submenu?.filter((item) => item.key !== CHARTING_NAVIGATION_KEY_APP_VIEW);
  }

  if (isQuestionnairesFeatureClosed) {
    const myPracticeMenuIndex = navigationConfig.findIndex(
      (item) => item.key === MY_PRACTICE_NAVIGATION_KEY_APP_VIEW
    );
    navigationConfig[myPracticeMenuIndex].submenu = navigationConfig[
      myPracticeMenuIndex
    ].submenu?.filter(
      (item) => item.key !== QUESTIONNAIRES_NAVIGATION_KEY_APP_VIEW
    );
  }

  if (isLoyaltyFeatureClosed) {
    const loyaltyMenuIndex = navigationConfig.findIndex(
      (item) => item.key === LOYALTY_NAVIGATION_KEY_APP_VIEW
    );
    if (loyaltyMenuIndex !== -1) {
      navigationConfig.splice(loyaltyMenuIndex, 1);
    }
  }

  if (isSpecialsFeatureClosed) {
    const marketingMenuIndex = navigationConfig.findIndex(
      (item) => item.key === MARKETING_NAVIGATION_KEY_APP_VIEW
    );
    if (marketingMenuIndex !== -1) {
      navigationConfig[marketingMenuIndex].submenu = navigationConfig[
        marketingMenuIndex
      ].submenu?.filter(
        (item) => item.key !== SPECIALS_NAVIGATION_KEY_APP_VIEW
      );
    }
  }

  if (isGiftCardsFeatureClosed) {
    const myPracticeMenuIndex = navigationConfig.findIndex(
      (item) => item.key === MY_PRACTICE_NAVIGATION_KEY_APP_VIEW,
    );
    if (myPracticeMenuIndex !== -1) {
      navigationConfig[myPracticeMenuIndex].submenu = navigationConfig[
        myPracticeMenuIndex
      ].submenu?.filter(
        (item) => item.key !== GIFT_CARDS_NAVIGATION_KEY_APP_VIEW,
      );
    }
  }

  const currentRouteInfo = utils.getRouteInfo(
    navigationConfig,
    location.pathname
  );
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = !screens.includes("lg");
  const isNavSide = navType === NAV_TYPE_SIDE;
  const isNavTop = navType === NAV_TYPE_TOP;
  const getLayoutGutter = () => {
    if (isNavTop || isMobile) {
      return 0;
    }
    return navCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH;
  };

  // We need to remove "position: relative" from "app-content" when we are on the landing page builder.
  const pathSegments = useLocation().pathname.split("/");
  const currentPage = pathSegments[2];
  const { status } = useThemeSwitcher();
  const { isMobileResolution } = useWindowSize(MOMBILE_BREAKPOINT);
  const [activeKey, setActiveKey] = useState();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const keyValue = params.get("key");
    if (keyValue === "messages") {
      setActiveKey("messages");
    } else {
      setActiveKey("");
    }
  }, [location]);

  const onToggle = () => {
    if (!isMobile) {
      toggleCollapsedNav(!navCollapsed);
    } else {
      onMobileNavToggle(!mobileNav);
    }
  };

  if (status === "loading") {
    return <Loading cover="page" />;
  }
  return (
    <Suspense fallback={<Spin />}>
      <Layout>
        <CallNotification />
        {isMobile && !mobileNav && (
          <div
            onClick={onToggle}
            className="nav-toggle-btn nav-toggle-btn-mobile"
          >
            <RightOutlined className="nav-icon" />
          </div>
        )}
        {!isMobile && (
          <div
            onClick={onToggle}
            className={`nav-toggle-btn`}
            style={{
              left:
                isNavSide && !navCollapsed
                  ? `${SIDE_NAV_WIDTH - 15}px`
                  : isNavSide && navCollapsed
                    ? `${SIDE_NAV_COLLAPSED_WIDTH - 15}px`
                    : "15px",
            }}
          >
            {navCollapsed ? (
              <RightOutlined className="nav-icon" />
            ) : (
              <LeftOutlined className="nav-icon" />
            )}
          </div>
        )}
        {isNavTop && !isMobile ? <TopNav routeInfo={currentRouteInfo} /> : null}
        <Layout className="app-container">
          {isNavSide && !isMobile ? (
            <SideNav
              localization={false}
              routeInfo={currentRouteInfo}
              navigationConfig={[...navigationConfig]}
            />
          ) : null}
          <Layout
            className="app-layout"
            style={{ paddingLeft: getLayoutGutter() }}
          >
            <div
              className={`app ${
                isMobileResolution && activeKey === "messages"
                  ? ""
                  : "app-content"
              } ${isNavTop ? "layout-top-nav" : ""} ${
                currentPage === LANDING_PAGE_BUILDER || currentPage === FORMS
                  ? "static"
                  : ""
              }`}
            >
              <PageHeader
                display={currentRouteInfo?.breadcrumb}
                title={currentRouteInfo?.title}
              />
              <Content>
                <AppViews />
              </Content>
            </div>
          </Layout>
        </Layout>
        {isMobile && <MobileNav navigationConfig={[...navigationConfig]} />}
      </Layout>
    </Suspense>
  );
};

const mapStateToProps = ({ theme }) => {
  const { navCollapsed, navType, locale, mobileNav } = theme;
  return { navCollapsed, navType, locale, mobileNav };
};

export default connect(mapStateToProps, {
  toggleCollapsedNav,
  onMobileNavToggle,
})(React.memo(AppLayout));
